/**
 * Anonymous support thread store — device-local custody of a BEARER SECRET.
 *
 * The ticket `access_token` grants read+reply on that thread to whoever holds
 * it, regardless of who is signed in. Rules this module exists to enforce:
 *
 *   1. NEVER in a shareable URL. The only URL that ever carries it is the
 *      emailed return link, and it travels in the hash fragment (never sent to
 *      any server, never in Referer). `consumeTokenFromHash` strips it from the
 *      address bar with replaceState so it does not stay in browser history.
 *   2. NEVER logged. Nothing here console.logs a token, and nothing that
 *      imports it may either.
 *   3. Device-scoped only. localStorage, under a key distinct from the session
 *      vault keys, so signing in/out never clobbers it and it is never sent
 *      with an auth request.
 */

const KEY = "cardmoji.support.threads.v1";
const MAX = 8;

export interface StoredThread {
  ticketId: string;
  token: string;
  subject: string;
  email: string | null;
  savedAt: string;
}

function read(): StoredThread[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (t): t is StoredThread =>
        !!t && typeof t.ticketId === "string" && typeof t.token === "string",
    );
  } catch {
    return [];
  }
}

function write(threads: StoredThread[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(KEY, JSON.stringify(threads.slice(0, MAX)));
  } catch {
    /* storage disabled — the emailed link is the durable copy */
  }
}

export function listStoredThreads(): StoredThread[] {
  return read().sort((a, b) => (a.savedAt < b.savedAt ? 1 : -1));
}

export function getStoredThread(ticketId: string): StoredThread | null {
  return read().find((t) => t.ticketId === ticketId) ?? null;
}

export function rememberThread(t: Omit<StoredThread, "savedAt">) {
  const others = read().filter((x) => x.ticketId !== t.ticketId);
  write([{ ...t, savedAt: new Date().toISOString() }, ...others]);
}

export function forgetThread(ticketId: string) {
  write(read().filter((t) => t.ticketId !== ticketId));
}

/**
 * Read `#t=<token>&id=<uuid>` from the current URL, persist it for this device,
 * and remove the fragment from the address bar (replaceState — no history
 * entry keeps the token). Returns the ticket id it adopted, if any.
 */
export function consumeTokenFromHash(): string | null {
  if (typeof window === "undefined") return null;
  const hash = window.location.hash.replace(/^#/, "");
  if (!hash) return null;
  const params = new URLSearchParams(hash);
  const token = params.get("t");
  const ticketId = params.get("id");
  // Strip it whether or not it parsed — a partial fragment must not linger.
  window.history.replaceState(
    null,
    "",
    window.location.pathname + window.location.search,
  );
  if (!token || !ticketId) return null;
  rememberThread({ ticketId, token, subject: "Support conversation", email: null });
  return ticketId;
}
